import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Header from './components/Header';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import About from './pages/About';
import Animations from './pages/Animations';
import Detail from './pages/Detail';
import Shorts from './pages/Shorts';
import UserProfile from './pages/UserProfile';
import Upload from './pages/Upload';
import MyPage from './pages/MyPage';
import Contact from './pages/Contact';
import Terms from './pages/legal/Terms';
import Privacy from './pages/legal/Privacy';
import Guidelines from './pages/legal/Guidelines';
import Rights from './pages/legal/Rights';
import Operator from './pages/legal/Operator';
import Credits from './pages/legal/Credits';
import ApiDocs from './pages/developers/ApiDocs';
import DevPortal from './pages/developers/DevPortal';
import UnityTool from './pages/tools/UnityTool';
import AdminConsole from './pages/admin/AdminConsole';
import NoAccess from './pages/NoAccess';
import Login from './pages/auth/Login';
import SignUp from './pages/auth/SignUp';
import Verify from './pages/auth/Verify';
import Reset from './pages/auth/Reset';
import ResetConfirm from './pages/auth/ResetConfirm';
import Authorize from './pages/oauth/Authorize';
import UnityAuth from './pages/unity/UnityAuth';
import { AuthProvider } from './auth/AuthContext';
import RequireLoginGate from './auth/RequireLoginGate';
import { DeveloperProvider } from './api/DeveloperContext';
import { VrmProvider } from './context/VrmContext';
import { FavoritesProvider } from './favorites/FavoritesContext';

// dev 環境限定の未公開機能フラグ。本番ビルドでは 'false' が渡される。
const shortsEnabled = import.meta.env.VITE_ENABLE_SHORTS !== 'false';

export default function App() {
  const location = useLocation();
  // Shorts（/shorts）はスマホの動画アプリのような全画面 UI のため、
  // アプリ共通の Header/Footer はここで非表示にする。
  const hideChrome = shortsEnabled && location.pathname === '/shorts';

  return (
    <AuthProvider>
      <FavoritesProvider>
        <VrmProvider>
          <DeveloperProvider>
            <div className="app">
              <ScrollToTop />
              {!hideChrome && <Header />}
              <main className="main">
                <RequireLoginGate>
                  <Routes>
                    <Route path="/" element={<Animations />} />
                    <Route path="/about" element={<About />} />
                    {/* 旧ルート名からのリダイレクト（外部からの既存リンク・ブックマーク互換のため維持）。tab / q などのクエリを保ったまま / へ移す。 */}
                    <Route path="/animations" element={<AnimationsRedirect />} />
                    <Route path="/animations/:id/edit" element={<Upload />} />
                    <Route path="/animations/:id" element={<Detail />} />
                    {shortsEnabled ? (
                      <Route path="/shorts" element={<Shorts />} />
                    ) : (
                      // フラグ off 時は既存 URL からのアクセス互換のためホームへリダイレクトする
                      <Route path="/shorts" element={<Navigate to="/" replace />} />
                    )}
                    <Route path="/users/:sub" element={<UserProfile />} />
                    <Route path="/upload" element={<Upload />} />
                    <Route path="/me" element={<MyPage />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/guidelines" element={<Guidelines />} />
                    <Route path="/rights" element={<Rights />} />
                    <Route path="/operator" element={<Operator />} />
                    <Route path="/credits" element={<Credits />} />
                    <Route path="/developers" element={<ApiDocs />} />
                    <Route path="/developers/portal/*" element={<DevPortal />} />
                    <Route path="/tools/unity" element={<UnityTool />} />
                    {/* 旧ルート名からのリダイレクト（外部からの既存リンク・ブックマーク互換のため維持） */}
                    <Route path="/developers/register" element={<Navigate to="/developers/portal" replace />} />
                    <Route path="/admin" element={<AdminConsole />} />
                    <Route path="/no-access" element={<NoAccess />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/signup" element={<SignUp />} />
                    <Route path="/verify" element={<Verify />} />
                    <Route path="/reset" element={<Reset />} />
                    <Route path="/reset/confirm" element={<ResetConfirm />} />
                    <Route path="/oauth/authorize" element={<Authorize />} />
                    <Route path="/unity/auth" element={<UnityAuth />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </RequireLoginGate>
              </main>
              {!hideChrome && <Footer />}
            </div>
          </DeveloperProvider>
        </VrmProvider>
      </FavoritesProvider>
    </AuthProvider>
  );
}

function NotFound() {
  return (
    <div className="container">
      <h2>ページが見つかりません</h2>
      <p>URL をご確認ください。</p>
    </div>
  );
}

// /animations は / （ホーム画面）へ統合されたため、?tab=mine や ?q=... などの
// クエリ文字列を保ったままリダイレクトする。<Navigate to="/" /> だけでは search が失われる。
function AnimationsRedirect() {
  const { search } = useLocation();
  return <Navigate to={{ pathname: '/', search }} replace />;
}
